import { useSection } from '@/lib/section'
import { SECTION_META } from '@/lib/sectionMeta'
import { cn } from '@/lib/cn'

/**
 * Toggle between the Onboarding (client) and ShopView School (internal) sections.
 * Only rendered for users who can access more than one section — clients never
 * see it.
 *
 * `onInk` renders the pass-2 sidebar treatment (design prototype "section
 * switch"): a 9px-radius ink-2 tray with two equal mono-label buttons using the
 * SHORT section names ("Onboarding" / "School" — the full "ShopView School"
 * doesn't fit the 248px rail), no icons, active = ink-3 fill + inset border.
 * The default styling (icons + full labels on a light pill) stays for surface
 * chrome like the mobile header row.
 */
export function SectionSwitcher({ onInk = false }: { onInk?: boolean }) {
  const { active, setActive, available } = useSection()

  if (available.length <= 1) return null

  return (
    <div
      role="tablist"
      aria-label="Content section"
      className={cn(
        'items-center',
        onInk
          ? 'flex w-full gap-[3px] rounded-[9px] border border-ink-border bg-ink-2 p-[3px]'
          : 'inline-flex gap-0.5 rounded-full border border-border bg-subtle p-0.5',
      )}
    >
      {available.map((section) => {
        const { label, shortLabel, icon: Icon } = SECTION_META[section]
        const isActive = active === section
        return (
          <button
            key={section}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setActive(section)}
            className={cn(
              'items-center whitespace-nowrap transition-colors duration-150 ease-out',
              onInk
                ? cn(
                    'flex h-[30px] flex-1 justify-center rounded-[7px] font-mono text-[12px] font-semibold tracking-[0.3px]',
                    isActive
                      ? 'bg-ink-3 text-white shadow-[inset_0_0_0_1px_var(--ink-border)]'
                      : 'text-ink-text-2 hover:text-ink-text',
                  )
                : cn(
                    'inline-flex h-8 gap-1.5 rounded-full px-3 text-[13px] font-semibold',
                    isActive
                      ? 'bg-surface text-brand shadow-[var(--shadow-xs)]'
                      : 'text-text-secondary hover:text-text-primary',
                  ),
            )}
          >
            {!onInk && <Icon className="h-4 w-4" aria-hidden strokeWidth={1.75} />}
            {onInk ? shortLabel : label}
          </button>
        )
      })}
    </div>
  )
}
